import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { FieldFilterBoxSelect } from '@components/filters/FieldFilterBoxSelect';
import {
  AddOperandButton,
  ArrayResolution,
  AvMessagePopover,
  boxWidth,
  CaseSensitiveButton,
  ConditionType,
  defaultOperator,
  defaultValue,
  DescriptorType,
  Filter,
  filterOperatorOptions,
  flipFilter,
  getCondition,
  getEmptyExpression,
  getExpressionType,
  getFilterOptionsWithLegacy,
  getIngressExpression,
  getOperator,
  getRelationalExpressionProps,
  getSourcesExp,
  isNullOperator,
  OpenSelect,
  operatorLabels,
  OPERATORS,
  OperatorType,
  RelationsProps,
  Select,
  selectFieldWidth,
  selectorWidth,
  StringConditionType,
  supportParserOperatorOptions,
  SwitchToggle,
} from '@fabric-ui/components';
import { ArrowDown, Delete, Search } from '@fabric-ui/icons';
import { InfoFull } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { emptyObject, generateOption } from '@fabric-ui/utils';
import { AutocompleteRenderGroupParams, Box, Divider, IconButton, List, ListItem, Typography, useTheme } from '@mui/material';
import { entityViewConfig } from '@utils/entityViewConfig';
import { useAvContext } from '../../context/AvContextProvider';
import { ANY_VALUE } from '../../views/Logs/logs.utils';
import { fieldTypeIconsMap, fieldTypeLabelMap, getFieldKey } from '../../views/ModelManagement/hooks';
import { FieldType, FieldTypeEnum, SchemaTypes, typeLabelMap } from '../../views/Sources/Mapping/types';
import WithDescriptionTooltip from '../WithDescriptionTooltip/WithDescriptionTooltip';
import FilterInput from './components/FilterInput';
import { TokenSuggestion } from './components/TokenFilterInput/types';
import DateTypeBox from './DateTypeBox';
import FilterTypeBox from './FilterTypeBox';
import UnifiedIngressToggle from './UnifiedIngressToggle';

export const FREE_SEARCH_FIELD = '__free_search__';

const isFreeSearchField = (fieldName?: string | null): boolean => fieldName === FREE_SEARCH_FIELD;

const escapeFreeTextSpecialChars = (value: string) => value.replace(/[\\_%]/g, '\\$&');
const unescapeFreeTextSpecialChars = (value: string) => value.replace(/\\([\\_%])/g, '$1');

const createFreeTextFilter = (value: string = ''): Filter => ({
  freeText: { stringValue: value ? `*${escapeFreeTextSpecialChars(value)}*` : '' },
});

const getFreeTextValue = (filter: Filter): string =>
  unescapeFreeTextSpecialChars((filter.freeText?.stringValue || '').replace(/^\*|\*$/g, ''));

export interface FieldFilterBoxProps {
  size: 'xSmall' | 'small';
  isVertical: boolean;
  rootOperator: 'or' | 'and' | 'expression';
  filter: Filter;
  setFilter: any;
  fields: any;
  isLoadingFields?: boolean;
  level?: number;
  onDelete: () => void;
  addLogicalExpression?: () => void;
  canAddNewFields?: boolean;
  canSelectFieldType?: boolean;
  supportParser?: boolean;
  timeRange?: Date[];
  operatorOptions?: typeof filterOperatorOptions;
  isIngressExp?: boolean;
  ingressModeProps?: { showToggle: boolean; multiSelect: boolean; isIconCollapsed?: boolean };
  customSx?: CSSProperties;
  defaultField?: string | null;
  fieldDescription?: string;
  showDeleteAddFilter?: boolean;
  activeProjName?: string;
  showDisableToggle?: boolean;
  initialEnable?: boolean;
  relationsProps?: RelationsProps;
  disableMessage?: string;
  isOverExpressionsLimit?: boolean;
  fieldSelectionMessage?: string;
  fieldOnly?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  fieldsListAdornment?: React.ReactNode;
  hasListTab?: boolean;
  isHistorical?: boolean;
  showAdvanced?: boolean;
  defaultMode?: 'list' | 'condition';
  excludeNullFromList?: boolean;
  tokenSuggestions?: TokenSuggestion[];
  enableQueryObj?: boolean;
  fieldExtraOptions?: Record<string, string[]>;
}

interface DeleteAddFilterProps {
  isVertical: boolean;
  size: 'xSmall' | 'small';
  level?: number;
  oppositeOperator: string;
  rootOperator: 'or' | 'and' | 'expression';
  onAdd?: () => void;
  onDelete: () => void;
  disableMessage?: string;
  disabled?: boolean;
}
const DeleteAddFilter: React.FC<DeleteAddFilterProps> = ({
  isVertical,
  size,
  level,
  oppositeOperator,
  rootOperator,
  onAdd,
  onDelete,
  disableMessage,
  disabled,
}) => (
  <Box
    className={isVertical ? 'option' : undefined}
    sx={{
      ...(isVertical
        ? { ...flex.colCenter, flexDirection: 'column' }
        : { ...flex.justifyBetweenCenter, ml: 1, flexGrow: 1, flexDirection: 'row-reverse' }),
      gap: 1,
      opacity: isVertical ? 0 : 1,
    }}>
    <IconButton onClick={onDelete} disabled={disabled} aria-label="Delete">
      <Delete />
    </IconButton>
    {onAdd && (
      <AddOperandButton
        isAdd
        hideText={isVertical}
        size={size}
        operator={(level === 0 ? oppositeOperator : rootOperator) as 'and' | 'or'}
        onClick={onAdd}
        disabled={disabled}
        disableMessage={disableMessage}
      />
    )}
  </Box>
);

const FieldFilterBox = (props: FieldFilterBoxProps) => {
  const {
    rootOperator,
    filter,
    setFilter,
    fields,
    isLoadingFields = false,
    level = 0,
    size,
    isVertical,
    onDelete,
    addLogicalExpression,
    canAddNewFields = false,
    canSelectFieldType = true,
    supportParser = false,
    timeRange,
    operatorOptions = filterOperatorOptions,
    ingressModeProps,
    isIngressExp = false,
    customSx = {},
    defaultField = '',
    fieldDescription,
    showDeleteAddFilter = true,
    activeProjName = entityViewConfig.Asset.projectionName,
    showDisableToggle = false,
    initialEnable = true,
    relationsProps = emptyObject as RelationsProps,
    disableMessage,
    isOverExpressionsLimit,
    fieldSelectionMessage,
    fieldOnly = false,
    disabled = false,
    readOnly = false,
    fieldsListAdornment,
    hasListTab = false,
    isHistorical,
    showAdvanced,
    defaultMode,
    excludeNullFromList,
    tokenSuggestions,
    enableQueryObj = false,
    fieldExtraOptions,
  } = props;
  const { transitions } = useTheme();
  const {
    accountEntities: { fieldMap },
  } = useAvContext();
  const isIngressMode = (ingressModeProps && !ingressModeProps.showToggle) || isIngressExp;

  const freeTextDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const freeTextUserSelectedRef = useRef(false);

  useEffect(() => () => clearTimeout(freeTextDebounceRef.current), []);

  const freeTextValueFromFilter = getFreeTextValue(filter);
  const [localFreeTextValue, setLocalFreeTextValue] = useState<string | null>(null);
  const freeTextInputValue = localFreeTextValue ?? freeTextValueFromFilter;
  const [enableFilter, setEnableFilter] = useState<boolean>(initialEnable);
  const sourcesValue = filter.and?.operands[0].expression?.arrayCondition?.underlying.stringCondition?.contains || null;
  const ingressFilterExp = filter.and?.operands[1];

  const actualFilterValue = ingressFilterExp || filter;

  const setIngressFilterExpression = newExp =>
    setFilter({
      ...filter,
      and: { ...filter.and, operands: [getSourcesExp(sourcesValue, newExp.expression.fieldName.split('.')[0]), newExp] },
    });
  const setCorrectFilter = newFilter => (isIngressMode ? setIngressFilterExpression(newFilter) : setFilter({ ...filter, ...newFilter }));

  const onChangeIngressToggle = () =>
    setFilter(
      isIngressMode
        ? flipFilter({ filter: actualFilterValue, isUberToIngress: false })
        : getIngressExpression(flipFilter({ filter: actualFilterValue, isUberToIngress: true }))
    );

  const typeCondition = (actualFilterValue.expression && getExpressionType(actualFilterValue.expression)) || ConditionType.string;
  const fieldName = actualFilterValue.freeText
    ? FREE_SEARCH_FIELD
    : (actualFilterValue.expression?.arrayCondition?.underlying || actualFilterValue.expression)?.fieldName ||
      actualFilterValue.and?.operands[0]?.expression?.fieldName ||
      actualFilterValue.or?.operands[0]?.expression?.fieldName;

  const shouldAutoFocusFreeText = freeTextUserSelectedRef.current && isFreeSearchField(fieldName);
  if (shouldAutoFocusFreeText) freeTextUserSelectedRef.current = false;

  const isRepeated = canSelectFieldType ? !!actualFilterValue.expression?.arrayCondition : fieldMap[fieldName!]?.repeated;
  const innerExpression = actualFilterValue.expression?.arrayCondition?.underlying || actualFilterValue.expression;
  const currentConditionValue =
    innerExpression?.[typeCondition] && Object.values(innerExpression[typeCondition]).find(v => typeof v === 'string');
  const isOperatorLockedByToken = !!tokenSuggestions?.some(t => t.value === currentConditionValue);
  const isInvalidToken =
    typeof currentConditionValue === 'string' &&
    currentConditionValue.startsWith('{') &&
    currentConditionValue.endsWith('}') &&
    !tokenSuggestions?.some(t => t.value === currentConditionValue);
  const { enableCaseSensitive = false, enableSide = false } = relationsProps;
  const setValueWithOperator = (token: TokenSuggestion) => {
    const [tokenOperator, tokenResolution] = token.operator.split('|');
    const underlying = {
      fieldName: innerExpression?.fieldName,
      [typeCondition]: { [tokenOperator]: token.value },
      ...getRelationalExpressionProps({ operator: tokenOperator, typeCondition, enableCaseSensitive, enableSide }),
    };
    const existingArrayCondition = actualFilterValue.expression?.arrayCondition;
    const useArrayCondition = tokenResolution || existingArrayCondition;
    setCorrectFilter({
      ...actualFilterValue,
      expression: useArrayCondition
        ? { arrayCondition: { underlying, resolution: tokenResolution ?? existingArrayCondition?.resolution } }
        : underlying,
    });
  };
  const conditionType = Object.keys(ConditionType).find(type => ConditionType[type] === typeCondition) || SchemaTypes.STRING;
  const typeName = `${conditionType}${isRepeated ? '[]' : ''}`;
  const iconsMap = fieldTypeIconsMap({ color: 'var(--color-content-base-secondary)' });
  const options = [
    ...[FieldType.Text, FieldType.DateTime, FieldType.Number, FieldType.Boolean].map(type => ({
      descriptorType: FieldTypeEnum[type],
      value: ConditionType[typeLabelMap[FieldTypeEnum[type]]],
    })),
    ...[FieldType.Text].map(type => ({
      // TODO: Use descriptor type only and derive ConditionType and isRepeated from it.
      descriptorType: [FieldTypeEnum[type]] as unknown as DescriptorType,
      value: ConditionType.array,
    })),
  ];

  const commonProps = {
    disabled,
    readOnly,
    size,
    isVertical,
    fields,
    filter: actualFilterValue,
    setFilter: isIngressMode ? setIngressFilterExpression : setFilter,
    showAdvanced,
  };
  const oppositeOperator = rootOperator === OperatorType.OR ? OperatorType.AND : OperatorType.OR;
  const operator = getCondition(actualFilterValue, typeCondition, supportParser ? StringConditionType.EQUALS : undefined);

  const setOperator = compoundValue => {
    const [value, resolution = ArrayResolution.ANY] = compoundValue.split('|');
    const [currValue] = operator.split('|');
    const underlying = {
      fieldName,
      [typeCondition]: {
        [value]: isNullOperator(value)
          ? {}
          : isNullOperator(currValue)
            ? defaultValue[typeCondition]?.(fieldMap[fieldName!])
            : (filter.expression?.arrayCondition?.underlying[typeCondition]?.[currValue] ??
              filter.expression?.[typeCondition]?.[currValue]),
      },
      ...getRelationalExpressionProps({
        operator: value,
        typeCondition,
        enableCaseSensitive: !!relationsProps.enableCaseSensitive,
        enableSide: !!relationsProps.enableSide,
      }),
    };

    const newFilter = {
      expression: isRepeated && typeCondition !== ConditionType.field ? { arrayCondition: { underlying, resolution } } : underlying,
    };
    setCorrectFilter(newFilter);
  };

  const onChangeEnableFilter = filterEnabled => {
    setEnableFilter(filterEnabled);
    setFilter(getEmptyExpression(filterEnabled ? defaultField : undefined));
  };

  const onSelectSource = (source: string[] | string) => {
    const newSourcesExp = getSourcesExp(ingressModeProps?.multiSelect ? (source as string[]).join() : source);
    setFilter({ ...filter, and: { ...filter.and, operands: [newSourcesExp, ingressFilterExp] } });
  };

  const onChangeFieldSelect = (value, { type = FieldType.Text, repeated }: { type?: string; repeated?: boolean } = {}) => {
    if (!value) return;
    if (isFreeSearchField(value)) {
      freeTextUserSelectedRef.current = true;
      setFilter(createFreeTextFilter());
      return;
    }

    const newTypeCondition = ConditionType[typeLabelMap[type]] || typeCondition;
    const defaultNewOperator = supportParser
      ? value === ANY_VALUE
        ? StringConditionType.CONTAINS
        : StringConditionType.EQUALS
      : repeated && newTypeCondition === ConditionType.field
        ? OPERATORS.in
        : undefined;
    const newExpression = getEmptyExpression(value, newTypeCondition, defaultNewOperator, fieldMap[value]).expression;
    const newFilter = {
      expression:
        filter.expression?.[newTypeCondition] && !repeated
          ? {
              ...filter.expression,
              fieldName: value,
            }
          : {
              ...newExpression,
              ...getRelationalExpressionProps({
                operator: defaultNewOperator ?? defaultOperator[newTypeCondition],
                typeCondition: newTypeCondition,
                enableSide: !!relationsProps.enableSide,
                enableCaseSensitive: !!relationsProps.enableSide,
              }),
            },
    };
    setCorrectFilter(newFilter);
  };

  // Excludes operators reserved for token selection only (e.g. 'in') from regular operator options.
  const tokenAwareOperatorOptions = operatorOptions[typeName]
    ? getFilterOptionsWithLegacy(operatorOptions[typeName], operator).filter(o => !tokenSuggestions?.some(t => t.operator === o.value))
    : [];

  const getSelectOperatorOptions = () => {
    if (
      typeCondition === ConditionType.date &&
      actualFilterValue?.expression?.dateCondition &&
      Object.keys(actualFilterValue.expression.dateCondition).length === 0 &&
      (actualFilterValue?.expression?.stringCondition?.empty || actualFilterValue?.expression?.stringCondition?.notEmpty)
    ) {
      return [];
    }
    return supportParser
      ? filter.expression?.fieldName === ANY_VALUE
        ? [
            { value: StringConditionType.CONTAINS, title: operatorLabels.contains },
            { value: StringConditionType.NOT_CONTAINS, title: operatorLabels.notContains },
          ]
        : supportParserOperatorOptions.string
      : tokenAwareOperatorOptions;
  };

  const deleteAddFilter = (
    <DeleteAddFilter
      size={size}
      isVertical={isVertical}
      level={level}
      oppositeOperator={oppositeOperator}
      rootOperator={rootOperator}
      onAdd={addLogicalExpression}
      onDelete={onDelete}
      disableMessage={disableMessage}
      disabled={isOverExpressionsLimit || disabled}
    />
  );
  const SelectComponent = canAddNewFields ? OpenSelect : Select;
  const labelFunc = ({ descriptorType }) => (
    <>
      {iconsMap[getFieldKey(descriptorType)]} {fieldTypeLabelMap[getFieldKey(descriptorType)]?.label}
    </>
  );

  const showListTab =
    fieldName && (typeCondition === ConditionType.population || typeCondition === ConditionType.string) && !isRepeated && hasListTab;
  const fieldType = fieldMap[isIngressMode ? (fieldName || '').replace('.sources', '') : fieldName!]?.type || conditionType;
  const selectedField = fields.find(f => f.value === fieldName);
  const startAdornment = () => {
    if (selectedField?.isFreeSearch || selectedField?.groupIcon) {
      return (
        <Box
          sx={{
            ...flex.itemsCenter,
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: 'var(--color-surface-base-tertiary)',
            flexShrink: 0,
            mr: '-8px',
            ml: '-8px',
          }}>
          {selectedField.isFreeSearch ? (
            <Search style={{ width: 12, height: 12 }} />
          ) : (
            (selectedField.selectedGroupIcon ?? selectedField.groupIcon)
          )}
        </Box>
      );
    }
    const selectedConditionType = isRepeated ? ConditionType.array : typeCondition;
    return canAddNewFields && canSelectFieldType ? (
      <Select
        variant="outlined"
        size="xSmall"
        showInput={false}
        value={selectedConditionType}
        onChange={(value, { descriptorType }: any) => {
          const underlyingTypeCondition = ConditionType[typeLabelMap[descriptorType]];
          setFilter({
            ...filter,
            expression: filter.expression?.[value]
              ? filter.expression
              : getEmptyExpression(
                  canSelectFieldType ? fieldName : filter.expression?.fieldName,
                  underlyingTypeCondition,
                  supportParser ? StringConditionType.EQUALS : undefined,
                  fieldMap[filter.expression!.fieldName!],
                  value === ConditionType.array
                ).expression,
          });
        }}
        options={options}
        getLabelFunc={labelFunc as any}
        selectIcon={
          <Box sx={{ ...flex.row, gap: '1px', height: '18px', color: 'var(--color-content-base-secondary)' }}>
            {iconsMap[getFieldKey(options.find(({ value }) => value === selectedConditionType)!.descriptorType!)]}
            <ArrowDown />
          </Box>
        }
        style={{ width: 52, pl: '3px', ml: '-12px', mr: '-8px' }}
        disabled={disabled}
      />
    ) : (
      <Box sx={{ ...flex.row, mr: '-8px', zIndex: 2 }}>
        {iconsMap[isRepeated ? JSON.stringify(fieldType) : (fieldType as string | number)]}
      </Box>
    );
  };

  const isEmptyDate = !!actualFilterValue.expression?.stringCondition?.empty;
  const isNotEmptyDate = !!actualFilterValue.expression?.stringCondition?.notEmpty;
  const isDateFilter = fieldName && [FieldType.Date, FieldType.DateTime].includes(fieldMap[fieldName]?.type as string);
  const shouldRenderSelect = !(isDateFilter && (isEmptyDate || isNotEmptyDate));

  return (
    <Box
      sx={{
        backgroundColor: !enableFilter && showDisableToggle ? 'var(--color-surface-interactive-primary-disabled)' : undefined,
        ':hover .option': {
          opacity: 1,
          transition: transitions.create(['opacity'], { duration: transitions.duration.standard }),
        },
        ...(isVertical ? { ...flex.row, gap: 1 } : {}),
      }}>
      <Box
        sx={{
          border: `1px solid var(--color-border-base-primary)`,
          width: isVertical ? boxWidth : 850,
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px',
          ...customSx,
        }}>
        {fieldDescription && (
          <Box sx={{ ml: 1.5, mt: 1.5, width: '95%', ...flex.justifyBetweenCenter }}>
            {fieldDescription}
            {showDisableToggle && <SwitchToggle value={enableFilter} onChange={onChangeEnableFilter} label={enableFilter ? 'On' : 'Off'} />}
          </Box>
        )}
        <Box sx={{ ...(isVertical ? flex.colItemsStart : flex.justifyStartCenter), p: isVertical ? '10px' : '12px', gap: 1 }}>
          {ingressModeProps && (
            <UnifiedIngressToggle
              onChangeIngressExp={() => onChangeIngressToggle()}
              sourcesValue={sourcesValue?.split(',') || []}
              isIngressExp={isIngressMode}
              onChangeSources={onSelectSource}
              activeProjName={activeProjName}
              showToggle={ingressModeProps.showToggle}
              multiSelect={ingressModeProps.multiSelect}
              isIconCollapsed={ingressModeProps.isIconCollapsed}
            />
          )}
          <SelectComponent
            style={{ width: isVertical ? selectFieldWidth : 250 }}
            size={size}
            isRequired
            placeholder="Select Field"
            value={fieldName}
            groupByFunc={({ group }) => group}
            onChange={onChangeFieldSelect}
            options={fields}
            loading={isLoadingFields}
            getValueFunc={isIngressMode && ingressModeProps ? ({ ingressValue }) => ingressValue : undefined}
            skeletonLoading
            optionsUseQueryProps={{ options: fields, isLoading: isLoadingFields }}
            startAdornment={
              !relationsProps.hideStartAdornment &&
              fieldName &&
              (selectedField?.isFreeSearch || selectedField?.groupIcon || !supportParser) &&
              startAdornment
            }
            disabled={!enableFilter || disabled}
            readOnly={readOnly}
            renderOption={(opt: (typeof fields)[number]) => (
              <WithDescriptionTooltip title={opt.description} badgeProps={{ sx: { width: '100%' } }} badgify>
                <Box sx={{ ...flex.row, gap: 1, alignItems: 'center' }}>
                  {opt.isFreeSearch && <Search style={{ width: 18, height: 18 }} />}
                  {opt.title}
                </Box>
              </WithDescriptionTooltip>
            )}
            inputToolbar={fieldsListAdornment}
            muiProps={{
              ...(canAddNewFields && { autoHighlight: true }),
              ...((fieldSelectionMessage || fields.some(f => f.groupIcon)) && {
                ...(fieldSelectionMessage && { getOptionDisabled: option => option.disabled === true }),
                renderGroup: ({ group, children, key }: AutocompleteRenderGroupParams) =>
                  !group ? (
                    <React.Fragment key={key}>{children}</React.Fragment>
                  ) : (
                    <ListItem key={key} sx={{ ...flex.col, p: 0 }}>
                      <Divider sx={{ width: '100%', py: 0.5 }} />
                      <Box
                        sx={{
                          ...flex.justifyBetweenCenter,
                          position: 'sticky',
                          top: -1,
                          width: '100%',
                          p: '0 8px',
                          height: 40,
                          zIndex: 1,
                          backgroundColor: 'var(--color-surface-base-primary)',
                        }}>
                        <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
                          {fields.find(f => f.group === group)?.groupIcon}
                          <Typography variant="h7">{group}</Typography>
                        </Box>
                        {fieldSelectionMessage && <AvMessagePopover icon={<InfoFull />} message={fieldSelectionMessage} asTooltip />}
                      </Box>
                      <List sx={{ p: 0, width: '100%' }}>{children}</List>
                    </ListItem>
                  ),
              }),
            }}
          />
          {!fieldOnly &&
            (isFreeSearchField(fieldName) ? (
              <FilterInput
                size={size}
                readOnly={readOnly}
                disabled={disabled}
                value={freeTextInputValue}
                onChange={value => {
                  setLocalFreeTextValue(value);
                  clearTimeout(freeTextDebounceRef.current);
                  freeTextDebounceRef.current = setTimeout(() => {
                    setFilter(createFreeTextFilter(value));
                    setLocalFreeTextValue(null);
                  }, 150);
                }}
                placeholder="Type here..."
                inputWidth={isVertical ? selectorWidth : 408}
                sx={{ maxWidth: 'unset' }}
                autoFocus={shouldAutoFocusFreeText}
                numberField={false}
                operator=""
                options={[]}
              />
            ) : (
              <>
                {shouldRenderSelect &&
                  (showListTab ? (
                    <FieldFilterBoxSelect
                      size={size}
                      fieldName={fieldName}
                      value={filter}
                      onChange={newFilter => {
                        (isIngressMode ? setIngressFilterExpression : setFilter)(newFilter);
                      }}
                      activeProjName={selectedField?.projectionName || activeProjName}
                      isHistorical={isHistorical}
                      showAdvanced={showAdvanced}
                      defaultMode={defaultMode}
                      excludeNullFromList={excludeNullFromList}
                      isVertical={isVertical}
                      enableQueryObj={enableQueryObj}
                      extraOptions={fieldName ? fieldExtraOptions?.[fieldName] : undefined}
                      timeRange={timeRange}
                    />
                  ) : isOperatorLockedByToken ? (
                    <Select
                      size={size}
                      style={{ width: isVertical ? selectorWidth : 150, input: { fontWeight: 600 } }}
                      muiProps={{ disablePortal: true }}
                      value={operator}
                      onChange={setOperator}
                      disabled
                      options={[{ value: operator, title: operatorLabels[operator] ?? operator }]}
                    />
                  ) : (
                    <Select
                      size={size}
                      style={{ width: isVertical ? selectorWidth : 150, input: { fontWeight: 600 } }}
                      isRequired
                      muiProps={{ disablePortal: true }}
                      placeholder="Select Operator"
                      value={getOperator(operator)}
                      onChange={setOperator}
                      disabled={!enableFilter || typeCondition === ConditionType.date || !fieldName || disabled}
                      options={getSelectOperatorOptions()}
                    />
                  ))}
                {!shouldRenderSelect || typeCondition === ConditionType.date ? (
                  <DateTypeBox {...commonProps} />
                ) : (
                  !showListTab &&
                  (typeName ? (
                    <FilterTypeBox
                      timeRange={timeRange}
                      typeCondition={typeCondition}
                      options={supportParser ? supportParserOperatorOptions.string : operatorOptions[typeName] || []}
                      supportParser={supportParser}
                      {...commonProps}
                      disabled={!enableFilter || disabled}
                      relationsProps={
                        typeCondition === ConditionType.enum
                          ? { additionalSelectOptions: fieldMap[fieldName!]?.originalType.values?.map(generateOption) }
                          : relationsProps
                      }
                      resetOperatorOnChange={isOperatorLockedByToken}
                      filterInputProps={{
                        tokenSuggestions,
                        onTokenSelect: setValueWithOperator,
                        error: isInvalidToken ? 'Invalid dynamic variable' : undefined,
                      }}
                    />
                  ) : (
                    <div>Not Implemented</div>
                  ))
                )}
              </>
            ))}
          {relationsProps.enableCaseSensitive && filter.expression?.fieldCondition?.equals && (
            <CaseSensitiveButton actualFilterValue={actualFilterValue} filter={filter} setFilter={setFilter} />
          )}
          {!isVertical && showDeleteAddFilter && !readOnly && deleteAddFilter}
        </Box>
      </Box>
      {isVertical && deleteAddFilter}
    </Box>
  );
};

export default FieldFilterBox;
